import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { formatearFecha } from '@/lib/fecha'
import { Boton } from '@/ui/Boton'
import { CampoFormulario, EntradaTexto, Selector } from '@/ui/CampoFormulario'
import { EstadoVacio } from '@/ui/EstadoVacio'
import { FilasEsqueleto } from '@/ui/Esqueleto'
import { InsigniaEstado } from '@/ui/InsigniaEstado'
import { ENCF, Monto } from '@/ui/Numero'
import { PanelLateral } from '@/ui/PanelLateral'
import { descargarAntiguedad, useCuentasPorPagar, useFijarVencimiento, useProveedores } from '@/dominio/compras/useCompras'
import type { DocumentoPorPagar } from '@/dominio/compras/tipos'
import { EncabezadoPantalla, Tarjeta } from './-pantalla'
import { BotonDescarga, Cifra, hoyIso } from './-contabilidad/comun'
import { ErrorCompras, TD, TH } from './-compras/comun'
import { PanelPago } from './-compras/Pagos'

/**
 * Cuentas por pagar (`docs/adr/0028` §4), del plan base: lo que se le debe a
 * cada proveedor —los recibidos a crédito (también los importados y los de
 * carga manual) y los gastos a crédito—, con su vencimiento y su antigüedad.
 *
 * Se eligen los documentos de un proveedor y se pagan juntos.
 */

export const Route = createFileRoute('/cuentas-por-pagar')({
  component: PantallaCuentasPorPagar,
})

const TONO_TRAMO = { por_vencer: 'neutral', '1_30': 'ambar', '31_60': 'ambar', '61_90': 'rojo', mas_90: 'rojo' } as const

function PantallaCuentasPorPagar() {
  const [corte, setCorte] = useState(hoyIso())
  const [proveedor, setProveedor] = useState('')
  const [texto, setTexto] = useState('')
  const [todos, setTodos] = useState(false)
  const proveedores = useProveedores()
  const cxp = useCuentasPorPagar({ corte, proveedor: proveedor || undefined, texto: texto.trim() || undefined, todos })
  const [elegidos, setElegidos] = useState<DocumentoPorPagar[]>([])
  const [pagando, setPagando] = useState(false)
  const [vencimiento, setVencimiento] = useState<DocumentoPorPagar | null>(null)
  const proveedorElegido = elegidos[0]?.proveedorId ?? null

  const alternar = (d: DocumentoPorPagar) =>
    setElegidos((es) => (es.some((e) => e.id === d.id) ? es.filter((e) => e.id !== d.id) : [...es, d]))

  return (
    <div className="flex flex-col gap-4">
      <EncabezadoPantalla
        titulo="Cuentas por pagar"
        descripcion="Lo que se debe a cada proveedor, por vencimiento. Elija documentos de un mismo proveedor y páguelos juntos."
        acciones={
          <Boton variante="primario" disabled={elegidos.length === 0 || proveedorElegido === null} onClick={() => setPagando(true)}>
            Pagar {elegidos.length > 0 ? `(${elegidos.length})` : ''}
          </Boton>
        }
      />

      {cxp.data ? (
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <Cifra etiqueta="Total por pagar" valor={cxp.data.saldo} />
          {cxp.data.tramos.map((t) => (
            <Cifra
              key={t.tramo}
              etiqueta={t.nombre}
              valor={t.saldo}
              nota={`${t.documentos} documento(s)`}
              tono={t.tramo === '61_90' || t.tramo === 'mas_90' ? (t.documentos > 0 ? 'rojo' : undefined) : undefined}
            />
          ))}
        </div>
      ) : null}

      <Tarjeta
        relleno={false}
        titulo="Documentos"
        acciones={<BotonDescarga etiqueta="Antigüedad en PDF" descargar={() => descargarAntiguedad(corte, 'pdf')} />}
      >
        <div className="grid gap-3 p-3 sm:grid-cols-4">
          <CampoFormulario etiqueta="Proveedor">
            {(a) => (
              <Selector {...a} value={proveedor} onChange={(e) => setProveedor(e.target.value)}>
                <option value="">Todos</option>
                {(proveedores.data ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nombre}
                  </option>
                ))}
              </Selector>
            )}
          </CampoFormulario>
          <CampoFormulario etiqueta="Buscar">
            {(a) => <EntradaTexto {...a} placeholder="NCF, RNC o nombre" value={texto} onChange={(e) => setTexto(e.target.value)} />}
          </CampoFormulario>
          <CampoFormulario etiqueta="Antigüedad al">
            {(a) => <EntradaTexto {...a} type="date" value={corte} onChange={(e) => setCorte(e.target.value)} />}
          </CampoFormulario>
          <label className="flex items-center gap-2 self-end pb-2 text-cuerpo text-gris-700">
            <input type="checkbox" checked={todos} onChange={(e) => setTodos(e.target.checked)} />
            Incluir los saldados
          </label>
        </div>
        <ErrorCompras error={cxp.error} />
        {!cxp.data ? (
          <div className="p-3">
            <FilasEsqueleto filas={6} columnas={7} />
          </div>
        ) : cxp.data.documentos.length === 0 ? (
          <EstadoVacio variante={texto || proveedor ? 'sin-resultados' : 'resuelto'} mensaje={texto || proveedor ? 'Nada coincide.' : 'No se le debe nada a nadie.'} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-cuerpo">
              <thead className="bg-gris-50">
                <tr className="border-b border-gris-300">
                  <th className={`${TH} w-8`} />
                  <th className={TH}>Proveedor</th>
                  <th className={TH}>Documento</th>
                  <th className={TH}>Fecha</th>
                  <th className={TH}>Vence</th>
                  <th className={`${TH} text-right`}>Total</th>
                  <th className={`${TH} text-right`}>Pagado</th>
                  <th className={`${TH} text-right`}>Saldo</th>
                  <th className={TH}>Antigüedad</th>
                </tr>
              </thead>
              <tbody>
                {cxp.data.documentos.map((d) => {
                  const marcado = elegidos.some((e) => e.id === d.id)
                  // Solo se juntan documentos de un mismo proveedor en un pago.
                  const otroProveedor = proveedorElegido !== null && d.proveedorId !== proveedorElegido
                  return (
                    <tr key={`${d.clase}-${d.id}`} className="h-[var(--alto-fila)] border-b border-gris-200 last:border-b-0">
                      <td className={TD}>
                        <input
                          type="checkbox"
                          aria-label={`Elegir ${d.ncf ?? 'el gasto'}`}
                          checked={marcado}
                          disabled={!d.porPagar || d.saldo === '0.00' || d.proveedorId === null || (otroProveedor && !marcado)}
                          onChange={() => alternar(d)}
                        />
                      </td>
                      <td className={TD}>
                        <span className="block max-w-[220px] truncate">{d.nombre ?? '—'}</span>
                        {d.rnc ? <span className="numero block text-etiqueta text-gris-600">{d.rnc}</span> : null}
                        {d.proveedorId === null ? (
                          <span className="block text-etiqueta text-ambar">Sin ficha de proveedor: créela en Gastos → Proveedores con este RNC para pagarlo.</span>
                        ) : null}
                      </td>
                      <td className={TD}>
                        {d.ncf ? <ENCF valor={d.ncf} abreviado copiable={false} /> : 'Gasto sin comprobante'}
                        <span className="block text-etiqueta text-gris-600">{d.clase === 'recibido' ? 'Recibido' : 'Gasto'}</span>
                      </td>
                      <td className={`${TD} numero`}>{formatearFecha(d.fecha)}</td>
                      <td className={`${TD} numero`}>
                        {d.clase === 'recibido' ? (
                          <button type="button" className="underline decoration-gris-400 underline-offset-2" onClick={() => setVencimiento(d)}>
                            {d.venceEl ? formatearFecha(d.venceEl) : 'Fijar'}
                          </button>
                        ) : d.venceEl ? (
                          formatearFecha(d.venceEl)
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className={`${TD} text-right`}>
                        <Monto valor={d.total} />
                      </td>
                      <td className={`${TD} text-right`}>
                        <Monto valor={d.pagado} />
                        {d.programado !== '0.00' ? (
                          <span className="block text-etiqueta text-azul">
                            <Monto valor={d.programado} /> programado
                          </span>
                        ) : null}
                      </td>
                      <td className={`${TD} text-right`}>
                        <Monto valor={d.saldo} />
                      </td>
                      <td className={TD}>
                        {d.saldo === '0.00' ? (
                          <InsigniaEstado tono="verde">saldado</InsigniaEstado>
                        ) : (
                          <InsigniaEstado tono={TONO_TRAMO[d.tramo]}>
                            {d.tramoNombre}
                            {d.diasVencido > 0 ? ` · ${d.diasVencido} d` : ''}
                          </InsigniaEstado>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Tarjeta>

      {cxp.data && cxp.data.anticipos.length > 0 ? (
        <Tarjeta titulo="Anticipos a proveedores" relleno={false}>
          <ul className="flex flex-col">
            {cxp.data.anticipos.map((a) => (
              <li key={a.proveedorId} className="flex items-center justify-between border-b border-gris-200 px-3 py-2 last:border-b-0">
                <span className="text-cuerpo text-gris-900">{a.nombre}</span>
                <Monto valor={a.disponible} mostrarMoneda />
              </li>
            ))}
          </ul>
        </Tarjeta>
      ) : null}

      {pagando && proveedorElegido ? (
        <PanelPago
          proveedorInicial={proveedorElegido}
          documentosIniciales={elegidos}
          onCerrar={() => setPagando(false)}
          onRegistrado={() => setElegidos([])}
        />
      ) : null}
      {vencimiento ? <PanelVencimiento documento={vencimiento} onCerrar={() => setVencimiento(null)} /> : null}
    </div>
  )
}

function PanelVencimiento({ documento, onCerrar }: { documento: DocumentoPorPagar; onCerrar: () => void }) {
  const fijar = useFijarVencimiento()
  const [vence, setVence] = useState(documento.venceEl ?? '')

  return (
    <PanelLateral
      abierto
      onCerrar={onCerrar}
      ancho="estrecho"
      titulo="Vencimiento del recibido"
      subtitulo="Sin fecha, vence a los días de crédito del proveedor."
      pie={
        <div className="flex justify-end gap-2">
          <Boton variante="discreto" onClick={onCerrar}>
            Cancelar
          </Boton>
          <Boton variante="primario" disabled={fijar.isPending} onClick={() => fijar.mutate({ recibidoId: documento.id, venceEl: vence || null }, { onSuccess: onCerrar })}>
            Guardar
          </Boton>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <CampoFormulario etiqueta="Vence el">
          {(a) => <EntradaTexto {...a} type="date" value={vence} min={documento.fecha} onChange={(e) => setVence(e.target.value)} />}
        </CampoFormulario>
        <ErrorCompras error={fijar.error} />
      </div>
    </PanelLateral>
  )
}
